import React, { useState } from 'react';
import { Descriptions, Button, Tag, Modal, Form, Input, DatePicker, Typography, Space, message } from 'antd';
import { EditOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { avaliacoesAdminService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import { STATUS_PERIODO_INFO } from './PeriodosAdminPainel';

const { Text } = Typography;

const formatarData = (valor) => (valor ? dayjs(valor).format('DD/MM/YYYY') : '—');

// Aba "Resumo" do detalhe do período (Fase 7). Ações dependem
// estritamente do status atual -- RASCUNHO permite editar/abrir; ABERTO
// permite encerrar; ENCERRADO é somente consulta. Nenhuma checagem aqui
// substitui o backend: os 409 reais (`avaliacao_periodo_service.py`)
// continuam sendo a autoridade final, só traduzidos para mensagens
// amigáveis.
const PeriodoResumoPainel = ({ periodo, onAtualizado }) => {
  const [modalEdicaoAberto, setModalEdicaoAberto] = useState(false);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [abrindo, setAbrindo] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const [form] = Form.useForm();

  const statusInfo = STATUS_PERIODO_INFO[periodo.status] || { label: periodo.status, color: 'default' };

  const abrirEdicao = () => {
    form.setFieldsValue({
      nome: periodo.nome,
      data_referencia: dayjs(periodo.data_referencia),
      data_abertura: dayjs(periodo.data_abertura),
      data_encerramento: periodo.data_encerramento ? dayjs(periodo.data_encerramento) : null,
    });
    setModalEdicaoAberto(true);
  };

  const handleSalvarEdicao = async (values) => {
    const abertura = values.data_abertura;
    const encerramento = values.data_encerramento;
    const referencia = values.data_referencia;

    if (abertura.isAfter(encerramento, 'day')) {
      message.error('A data de abertura não pode ser posterior à data de encerramento.');
      return;
    }
    if (referencia.isAfter(encerramento, 'day')) {
      message.error('A data de referência não pode ser posterior à data de encerramento.');
      return;
    }

    setSalvandoEdicao(true);
    try {
      await avaliacoesAdminService.atualizarPeriodo(periodo.id, {
        nome: values.nome,
        data_abertura: abertura.format('YYYY-MM-DD'),
        data_encerramento: encerramento.format('YYYY-MM-DD'),
        data_referencia: referencia.format('YYYY-MM-DD'),
      });
      message.success('Período atualizado com sucesso.');
      setModalEdicaoAberto(false);
      onAtualizado();
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível atualizar o período.');
      message.error(mensagem);
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const executarAbertura = async () => {
    setAbrindo(true);
    try {
      await avaliacoesAdminService.abrirPeriodo(periodo.id);
      message.success('Período aberto com sucesso.');
      onAtualizado();
    } catch (err) {
      const detalhe = err?.response?.data?.detail;
      if (typeof detalhe === 'string' && detalhe.toLowerCase().includes('critérios ativos')) {
        message.error('Cadastre ou ative pelo menos um critério antes de abrir o período.');
      } else {
        const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível abrir o período.');
        message.error(mensagem);
      }
    } finally {
      setAbrindo(false);
    }
  };

  const handleAbrir = () => {
    Modal.confirm({
      title: 'Abrir período?',
      content:
        'Ao abrir o período, os critérios ativos serão congelados para esta avaliação e as datas não poderão mais ser alteradas.',
      okText: 'Abrir período',
      cancelText: 'Cancelar',
      onOk: executarAbertura,
    });
  };

  const executarEncerramento = async () => {
    setEncerrando(true);
    try {
      await avaliacoesAdminService.encerrarPeriodo(periodo.id);
      message.success('Período encerrado com sucesso.');
      onAtualizado();
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível encerrar o período.');
      message.error(mensagem);
    } finally {
      setEncerrando(false);
    }
  };

  const handleEncerrar = () => {
    Modal.confirm({
      title: 'Encerrar período?',
      content: 'Deseja encerrar este período de avaliação? Após o encerramento, os professores não poderão mais alterar as avaliações.',
      okText: 'Encerrar período',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: executarEncerramento,
    });
  };

  return (
    <div>
      <Descriptions column={1} bordered size="small" style={{ maxWidth: 520 }}>
        <Descriptions.Item label="Nome">{periodo.nome}</Descriptions.Item>
        <Descriptions.Item label="Data de referência">{formatarData(periodo.data_referencia)}</Descriptions.Item>
        <Descriptions.Item label="Abertura">{formatarData(periodo.data_abertura)}</Descriptions.Item>
        <Descriptions.Item label="Encerramento">{formatarData(periodo.data_encerramento)}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
        </Descriptions.Item>
      </Descriptions>

      <Space style={{ marginTop: 16 }}>
        {periodo.status === 'RASCUNHO' && (
          <>
            <Button icon={<EditOutlined />} onClick={abrirEdicao}>
              Editar
            </Button>
            <Button type="primary" icon={<UnlockOutlined />} loading={abrindo} onClick={handleAbrir}>
              Abrir período
            </Button>
          </>
        )}
        {periodo.status === 'ABERTO' && (
          <Button danger icon={<LockOutlined />} loading={encerrando} onClick={handleEncerrar}>
            Encerrar período
          </Button>
        )}
        {periodo.status === 'ENCERRADO' && (
          <Text type="secondary">Período encerrado — somente consulta.</Text>
        )}
      </Space>

      <Modal
        title="Editar período"
        open={modalEdicaoAberto}
        onOk={() => form.submit()}
        onCancel={() => setModalEdicaoAberto(false)}
        confirmLoading={salvandoEdicao}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSalvarEdicao}>
          <Form.Item name="nome" label="Nome" rules={[{ required: true, message: 'Nome é obrigatório' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="data_referencia"
            label="Data de referência"
            help="Define quais matrículas/turmas serão consideradas para esta avaliação."
            rules={[{ required: true, message: 'Data de referência é obrigatória' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="data_abertura"
            label="Data de abertura"
            rules={[{ required: true, message: 'Data de abertura é obrigatória' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="data_encerramento"
            label="Data de encerramento"
            rules={[{ required: true, message: 'Data de encerramento é obrigatória' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PeriodoResumoPainel;
